// Story 5.6 — TanStack Query mutation hook for POST
// /api/v1/marketplace/listings/sealed-dossier/{listing_id}/cancel.
//
// Sibling to useCancelShareListing — same shape with the URL + response
// surface swap. Response envelope carries sealed_dossier_id (NOT share_id).

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface CancelSealedDossierListingVariables {
  listing_id: string;
}

export interface CancelSealedDossierListingResponseData {
  listing_id: string;
  sealed_dossier_id: string;
  status: string;
  cancelled_at: string;
}

export interface CancelSealedDossierListingError {
  code: string;
  message: string;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postCancelSealedDossierListing(
  variables: CancelSealedDossierListingVariables,
): Promise<CancelSealedDossierListingResponseData> {
  const response = await fetch(
    `/api/v1/marketplace/listings/sealed-dossier/${variables.listing_id}/cancel`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": freshIdempotencyKey(),
      },
      body: "{}",
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: CancelSealedDossierListingError = {
      code: "CANCEL_LISTING_FAILED",
      message: `Cancel failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const body = (await response.json()) as {
    data: CancelSealedDossierListingResponseData;
  };
  return body.data;
}

export function useCancelSealedDossierListing(): UseMutationResult<
  CancelSealedDossierListingResponseData,
  CancelSealedDossierListingError,
  CancelSealedDossierListingVariables
> {
  return useMutation<
    CancelSealedDossierListingResponseData,
    CancelSealedDossierListingError,
    CancelSealedDossierListingVariables
  >({
    mutationFn: postCancelSealedDossierListing,
  });
}
